import type { Moment } from 'moment/moment';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import type { ModalProps } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { CheckCircle, CancelOutlined } from '@mui/icons-material';

import { Transaction } from 'containers/GivingAccountPage/types';
import { ProviderType, useMarkGivingAccountWithdrawalAsProcessedMutation } from 'generated/graphql';
import { currencies, CurrencyCode } from 'utils/currencyFormat';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { controlledDatePicker } from 'components/ReactHookForm/controlledDatePicker';
import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import { controlledFileUpload } from 'components/ReactHookForm/controlledFileUpload';

export const START_DATE_OF_BATCH_FEATURE = '2020-01-01';

type FieldValues = {
  processedAt: Moment;
  currencyCode: string;
  totalAccountCurrency?: string | number | null;
  identifier?: string | null;
  description: string;
  note: string;
  receiptUrl: string;
};

const CurrencyCodeInput = controlledTextField<FieldValues>()('currencyCode', {
  rules: { required: true },
});

const TotalAccountCurrencyInput = controlledTextField<FieldValues>()('totalAccountCurrency', {
  rules: { required: true, min: 0 },
});

const IdentifierInput = controlledTextField<FieldValues>()('identifier');

const DescriptionInput = controlledTextField<FieldValues>()('description');

const NoteInput = controlledTextField<FieldValues>()('note');

const ReceiptUrlInput = controlledFileUpload<FieldValues>()('receiptUrl');

export const MarkGivingAccountWithdrawalAsProcessedModal = ({
  givingAccountWithdrawal,
  accountCurrency,
  accountProvider,
  style,
  onClickClose = (): void => {},
  ...props
}: {
  givingAccountWithdrawal: Extract<Transaction, { __typename: 'GivingAccountWithdrawal' }>;
  accountCurrency: CurrencyCode;
  accountProvider: ProviderType;
  onClickClose: () => void;
} & Omit<ModalProps, 'children'>) => {
  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors, isValid, isSubmitting },
    getValues,
    control,
    reset,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      processedAt: moment(),
      currencyCode: accountCurrency,
      totalAccountCurrency:
        givingAccountWithdrawal?.currencyCode === accountCurrency
          ? givingAccountWithdrawal.total
          : '',
      identifier: givingAccountWithdrawal?.identifier,
      description: givingAccountWithdrawal?.description || '',
      note: '',
      receiptUrl: '',
    },
  });
  const [
    markGivingAccountWithdrawalAsProcessedMutation,
    { error: markGivingAccountWithdrawalAsProcessedMutationError },
  ] = useMarkGivingAccountWithdrawalAsProcessedMutation({
    refetchQueries: ['givingAccountSummary', 'givingAccountTransactions'],
  });

  useEffect(() => {
    reset();
  }, [props.open]);

  const formValues = getValues();
  const handleSubmit = reactHookFormHandleSubmit(({ currencyCode: _currencyCode, ...data }) =>
    markGivingAccountWithdrawalAsProcessedMutation({
      variables: {
        id: givingAccountWithdrawal.id,
        data: {
          identifier: data.identifier,
          description: data.description,
          note: data.note,
          receiptUrl: data.receiptUrl,
          processedAt: data.processedAt.toISOString(),
          totalAccountCurrency: Number(data.totalAccountCurrency),
        },
      },
    })
  );

  const minProcessedAt = moment.utc(givingAccountWithdrawal.createdAt).startOf('day');
  const ProcessedAtInput = controlledDatePicker<FieldValues>()('processedAt', {
    rules: {
      required: true,
      validate: {
        minDate: (date) =>
          moment.utc(givingAccountWithdrawal.createdAt).startOf('day').isSameOrBefore(date),
        maxDate: (date) => moment.utc().isSameOrAfter(moment.utc(date).startOf('day')),
      },
    },
  });

  if (!props.open) {
    return null;
  }

  return (
    <Modal
      disableAutoFocus
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      {...props}
    >
      <ModalContents
        onClickClose={onClickClose}
        body={
          <>
            <ModalTitle>Confirm withdrawal</ModalTitle>
            <Box paddingTop={4}>
              <form onSubmit={handleSubmit} disabled={isSubmitting}>
                <Stack spacing={3}>
                  <ProcessedAtInput
                    controllerProps={{ control }}
                    label="Processed at"
                    inputFormat="yyyy-MM-DD"
                    disableFuture
                    minDate={minProcessedAt}
                    helperText={
                      errors.processedAt?.message ||
                      'The date the processor confirmed transfer of the funds'
                    }
                  />
                  <CurrencyCodeInput
                    controllerProps={{ control }}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled
                    helperText={
                      errors.currencyCode?.message || `Locked to the withdrawal account's currency`
                    }
                    label="Payment currency"
                    select
                  >
                    {Object.values(currencies).map((currency) => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {currency.label}
                      </MenuItem>
                    ))}
                  </CurrencyCodeInput>
                  <TotalAccountCurrencyInput
                    controllerProps={{ control }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {currencies[formValues.currencyCode]?.symbol}
                        </InputAdornment>
                      ),
                      inputProps: {
                        step: 0.01,
                      },
                    }}
                    helperText={errors.totalAccountCurrency?.message}
                    label="Amount deducted"
                    type="number"
                  />
                  <IdentifierInput
                    controllerProps={{ control }}
                    helperText={
                      errors.identifier?.message ||
                      `The transaction identifier from ${accountProvider}, if available`
                    }
                    label="Transaction identifier"
                    type="text"
                  />
                  <DescriptionInput
                    controllerProps={{ control }}
                    helperText={
                      errors.description?.message ||
                      'What we show in the transactions table. Visible to customers'
                    }
                    label="Description"
                    type="text"
                  />
                  <NoteInput
                    controllerProps={{ control }}
                    helperText={
                      errors.note?.message ||
                      'Internal notes for the Deed Finance team. Not visible to customers'
                    }
                    multiline
                    minRows={3}
                    label="Notes"
                    type="text"
                  />
                  <ReceiptUrlInput
                    controllerProps={{ control }}
                    helperText={
                      errors.receiptUrl?.message ||
                      `The screenshot/print out of the confirmation from ${accountProvider}`
                    }
                    linkPreview
                    s3Acl="private"
                    s3Bucket="secure"
                    label="Transaction receipt/documentation"
                  />
                  {markGivingAccountWithdrawalAsProcessedMutationError && (
                    <Alert severity="error">
                      Failed to confirm withdrawal:
                      <br />
                      <br />
                      {markGivingAccountWithdrawalAsProcessedMutationError?.message}
                    </Alert>
                  )}
                </Stack>
              </form>
            </Box>
          </>
        }
        actions={
          <>
            <ModalButton
              variant="outlined"
              disabled={isSubmitting}
              onClick={(event: React.MouseEvent): void => {
                onClickClose();
                event.stopPropagation();
              }}
              endIcon={<CancelOutlined />}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={!isValid || isSubmitting}
              onClick={async (event: React.MouseEvent): Promise<void> => {
                try {
                  await handleSubmit();
                  onClickClose();
                  event.stopPropagation();
                } catch (error) {
                  // @NOTE-RT: Just swallow this error silently
                }
              }}
              endIcon={<CheckCircle />}
            >
              Confirm withdrawal
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};

export default MarkGivingAccountWithdrawalAsProcessedModal;
